import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import BookManagement from './BookManagement';
import BillingManagement from './BillingManagement';
import CustomerImport from './CustomerImport';
import DatabaseExport from './DatabaseExport';
import TicketManagement from './TicketManagement';
import MaintenanceManagement from './MaintenanceManagement';
import StaffManagement from './StaffManagement';
import RulesManagement from './RulesManagement';
import GameCancellationManagement from './GameCancellationManagement';
import LiveFraudManagement from './LiveFraudManagement';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: colors.shadowCard };

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { document.title = 'Admin | CakeStakes'; }, []);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getDashboard();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'books', label: 'Books' },
    { id: 'tickets', label: 'Tickets' },
    { id: 'billing', label: 'Billing' },
    { id: 'import', label: 'Import' },
    { id: 'export', label: 'Export' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'staff', label: 'Staff' },
    { id: 'rules', label: 'Rules & FAQ' },
    { id: 'cancellation', label: 'Game Void' },
    { id: 'fraud', label: 'Live Fraud' },
  ];

  const renderOverview = () => {
    if (loading) return <div style={{ color: colors.textMuted, textAlign: 'center', padding: '3rem' }}>Loading...</div>;
    if (error) return <div style={{ color: colors.negative, background: 'rgba(220,53,69,0.1)', borderRadius: 6, padding: '1rem' }}>{error}</div>;
    if (!stats) return null;

    return (
      <div>
        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Books', value: stats.stats.total_books },
            { label: 'Total Agents', value: stats.stats.total_agents },
            { label: 'Total Customers', value: stats.stats.total_customers },
            { label: 'Active Users (Period)', value: stats.stats.total_active_users },
          ].map(s => (
            <div key={s.label} style={{ ...card, textAlign: 'center', marginBottom: 0 }}>
              <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>{s.label}</p>
              <p style={{ color: colors.textPrimary, fontSize: '2rem', fontWeight: 700, margin: 0 }}>{s.value}</p>
            </div>
          ))}
          <div style={{ ...card, textAlign: 'center', marginBottom: 0, background: 'rgba(191,149,63,0.15)', border: `1px solid ${colors.gold}` }}>
            <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Period Revenue</p>
            <p style={{ color: colors.gold, fontSize: '2rem', fontWeight: 700, margin: 0 }}>${stats.stats.current_period_revenue.toFixed(2)}</p>
          </div>
        </div>

        {/* Current Period */}
        <div style={card}>
          <h3 style={{ color: colors.textPrimary, margin: '0 0 0.75rem 0', fontSize: '0.95rem' }}>Current Billing Period</h3>
          {stats.current_period && (
            <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
              <p style={{ margin: '0.25rem 0' }}><span style={{ color: colors.textPrimary }}>Start:</span> {new Date(stats.current_period.period_start).toLocaleDateString()}</p>
              <p style={{ margin: '0.25rem 0' }}><span style={{ color: colors.textPrimary }}>End:</span> {new Date(stats.current_period.period_end).toLocaleDateString()}</p>
              <p style={{ margin: '0.25rem 0' }}><span style={{ color: colors.textPrimary }}>Status:</span> <span style={{ color: stats.current_period.is_finalized ? colors.textFaint : colors.odds }}>{stats.current_period.is_finalized ? 'Finalized' : 'Active'}</span></p>
            </div>
          )}
        </div>

        {/* Books Summary */}
        <div style={card}>
          <h3 style={{ color: colors.textPrimary, margin: '0 0 0.75rem 0', fontSize: '0.95rem' }}>Books Summary</h3>
          {stats.billing_summary?.books?.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                    {['Book', 'Active Users', 'Price/User', 'Total', 'Status'].map(h => (
                      <th key={h} style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.billing_summary.books.map((book, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)' }}>
                      <td style={{ padding: '0.5rem', color: colors.textPrimary }}>{book.book_name}</td>
                      <td style={{ padding: '0.5rem', color: colors.gold }}>{book.active_user_count}</td>
                      <td style={{ padding: '0.5rem', color: colors.gold }}>${(book.price_per_user || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.5rem', color: colors.gold, fontWeight: 600 }}>${(book.total_amount || 0).toFixed(2)}</td>
                      <td style={{ padding: '0.5rem' }}>
                        <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 20, background: book.is_paid ? 'rgba(40,167,69,0.2)' : book.is_preview ? 'rgba(255,193,7,0.2)' : 'rgba(220,53,69,0.2)', color: book.is_paid ? colors.odds : book.is_preview ? colors.pending : colors.negative }}>
                          {book.is_preview ? 'Preview' : book.is_paid ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p style={{ color: colors.textMuted }}>No books configured</p>}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'books': return <BookManagement />;
      case 'tickets': return <TicketManagement />;
      case 'billing': return <BillingManagement />;
      case 'import': return <CustomerImport />;
      case 'export': return <DatabaseExport />;
      case 'maintenance': return <MaintenanceManagement />;
      case 'staff': return <StaffManagement />;
      case 'rules': return <RulesManagement />;
      case 'cancellation': return <GameCancellationManagement />;
      case 'fraud': return <LiveFraudManagement />;
      default: return renderOverview();
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, color: colors.textPrimary }}>
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${colors.goldMuted}` }}>
        <h1 style={{ margin: 0, color: colors.textPrimary, fontSize: '1.2rem', fontWeight: 700 }}>Admin Panel</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>Hi, <strong style={{ color: colors.textPrimary }}>{user.name}</strong></span>
          <button onClick={onLogout} style={{ background: colors.cardBg, border: 'none', color: colors.textPrimary, borderRadius: 4, padding: '0.4rem 1rem', cursor: 'pointer', fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
            Logout
          </button>
        </div>
      </header>

      {/* Nav */}
      <nav style={{ display: 'flex', flexWrap: 'wrap', background: colors.cardBg, borderBottom: `1px solid ${colors.goldMuted}`, padding: '0 0.75rem', gap: 0 }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.85rem 1.2rem',
              background: 'none',
              border: 'none',
              color: activeTab === tab.id ? colors.textPrimary : colors.textMuted,
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? `3px solid ${colors.gold}` : '3px solid transparent',
              fontFamily,
              fontSize: '0.9rem',
              fontWeight: activeTab === tab.id ? 700 : 400,
              transition: 'all 0.15s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main style={{ padding: '1.5rem', maxWidth: 1400, margin: '0 auto' }}>
        {renderContent()}
      </main>
    </div>
  );
};

export default AdminDashboard;
